'use client';

import React, { useState } from 'react';
import { Copy, Check, Zap } from 'lucide-react';
import { toast } from 'sonner';

interface Account {
  role: string;
  email: string;
  password: string;
}

interface Props {
  accounts: Account[];
  onSelect: (email: string, password: string) => void;
}

export default function DemoCredentials({ accounts, onSelect }: Props) {
  const [copiedField, setCopiedField] = useState<string | null>(null);

  const handleCopy = (value: string, key: string) => {
    navigator.clipboard.writeText(value);
    setCopiedField(key);
    toast.success('Copied to clipboard');
    setTimeout(() => setCopiedField(null), 2000);
  };

  return (
    <div className="mt-8 border border-brand-violet/20 rounded-xl bg-brand-violet/5 p-4">
      <div className="flex items-center gap-2 mb-3">
        <Zap size={14} className="text-brand-violet" />
        <span className="text-xs font-semibold text-brand-violet uppercase tracking-wider">Demo Accounts</span>
      </div>
      <div className="space-y-2">
        {accounts.map((acc) => (
          <div key={`demo-${acc.role}`} className="bg-dark-card rounded-lg p-3 border border-dark-border">
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs font-semibold text-gray-300">{acc.role}</span>
              <button
                onClick={() => {
                  onSelect(acc.email, acc.password);
                  toast.success(`${acc.role} credentials filled`);
                }}
                className="text-xs text-brand-cyan hover:text-brand-cyan-light font-medium transition-colors px-2 py-0.5 rounded bg-brand-cyan/10 hover:bg-brand-cyan/20"
              >
                Use this
              </button>
            </div>
            <div className="space-y-1">
              <div className="flex items-center justify-between">
                <span className="text-xs text-gray-500 font-mono-data">{acc.email}</span>
                <button onClick={() => handleCopy(acc.email, `${acc.role}-email`)} className="text-gray-600 hover:text-gray-300 transition-colors">
                  {copiedField === `${acc.role}-email` ? <Check size={12} className="text-green-400" /> : <Copy size={12} />}
                </button>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-xs text-gray-500 font-mono-data">{acc.password}</span>
                <button onClick={() => handleCopy(acc.password, `${acc.role}-pass`)} className="text-gray-600 hover:text-gray-300 transition-colors">
                  {copiedField === `${acc.role}-pass` ? <Check size={12} className="text-green-400" /> : <Copy size={12} />}
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}